import { defineStore } from 'pinia'
import { ref } from 'vue'

import { vuetify } from '@/app/ui/vuetify'

/**
 * Light/dark theme preference.
 *
 * BRD-010: users may choose light or dark; the preference is persisted per
 * user/device as appropriate. This is a display preference only - it is never
 * business state, so browser storage is the correct home for it. Vuetify resolves
 * `system` against `prefers-color-scheme` and follows OS changes live.
 */
export type ThemeChoice = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'fumbo.theme'
const CHOICES: readonly ThemeChoice[] = ['light', 'dark', 'system']

function readStored(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return (CHOICES as readonly string[]).includes(stored ?? '')
      ? (stored as ThemeChoice)
      : 'system'
  } catch {
    return 'system'
  }
}

export const useThemeStore = defineStore('theme', () => {
  const choice = ref<ThemeChoice>('system')

  function apply(): void {
    void vuetify.theme.change(choice.value)
    // The pre-paint script in index.html keys the page background off this class.
    const dark =
      choice.value === 'dark' ||
      (choice.value === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    document.documentElement.classList.toggle('dark', dark)
  }

  function set(next: ThemeChoice): void {
    choice.value = next
    try {
      if (next === 'system') localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Storage may be unavailable (private mode). The choice still applies for
      // this session.
    }
    apply()
  }

  function initialise(): void {
    choice.value = readStored()
    apply()
  }

  return { choice, set, initialise }
})
